import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import axios from 'axios'
import FileSaver from 'file-saver'
import classnames from 'classnames'
import ScrollBar from 'react-perfect-scrollbar'
import 'react-perfect-scrollbar/dist/css/styles.css'
import { AlertModal } from 'components/ModalTypes'
import * as userActions from '../store/actions/user'
import Input from '../components/Input'
import Loading from '../components/Loading'
import ConsentLegalText from '../components/ConsentLegalText'
import { generateConsentPDF } from '../helpers/consentPDF'

const API_URL = process.env.REACT_APP_API_URL

const codesMatch = (a, b) =>
  !!a && !!b && a.toUpperCase() === b.toUpperCase()

const StreamlinedConsent = ({ response, match }) => {
  const dispatch = useDispatch()
  const { token } = match.params
  const responseCode = (response && response.code) || ''

  const [step, setStep] = useState(response.step)
  const [eConsentSettings] = useState(response.e_consent_settings)
  const [consent, setConsent] = useState(null)
  const [authCode, setAuthCode] = useState('')
  const [codeError, setCodeError] = useState(null)
  const [downloading, setDownloading] = useState(false)
  const [cancelModal, setCancelModal] = useState(false)
  const [cancelled, setCancelled] = useState(false)

  const triggerAppraisalDownload = useCallback(() => {
    setDownloading(true)
    axios({
      method: 'GET',
      url: `${API_URL}/order/submission/${token}`,
      responseType: 'blob',
    }).then((res) => {
      FileSaver.saveAs(new Blob([res.data]), 'appraisal_report.pdf')
      setDownloading(false)
      setStep(3)
    }).catch(() => {
      setDownloading(false)
      setCodeError('Could not download the appraisal. Please try again.')
    })
  }, [token])

  // Deep-link auto-download. Runs whenever step becomes 2 (so it covers
  // both "land directly on step 2" and "consent then advance to step 2"
  // paths). The query param is stripped after first read so a refresh
  // doesn't replay.
  useEffect(() => {
    if (step !== 2) {
      return
    }
    const params = new URLSearchParams(window.location.search)
    const prefilled = params.get('code')
    if (!prefilled) {
      return
    }

    window.history.replaceState(
      {},
      '',
      `${window.location.origin}${window.location.pathname}`
    )

    if (codesMatch(prefilled, responseCode)) {
      triggerAppraisalDownload()
    } else {
      setAuthCode(prefilled)
      setCodeError(
        'That link contained an invalid code. Enter your code below or download a fresh test PDF.'
      )
    }
  }, [step, responseCode, triggerAppraisalDownload])

  const handleAuthCodeChange = (value) => {
    setAuthCode(value.trim())
    setCodeError(null)
  }

  const handleDownloadTestPDF = () => {
    try {
      const result = generateConsentPDF({ code: responseCode })
      if (result && typeof result.catch === 'function') {
        result.catch(() => {
          setCodeError('Could not open the authorization code PDF. Please try again.')
        })
      }
    } catch (err) {
      setCodeError('Could not open the authorization code PDF. Please try again.')
    }
  }

  const handleSubmit = () => {
    if (!authCode || downloading) {
      return
    }
    if (codesMatch(authCode, responseCode)) {
      triggerAppraisalDownload()
    } else {
      setCodeError('That code is incorrect. Please try again.')
    }
  }

  const handleConsentSubmit = () => {
    if (!consent) {
      setCancelModal(true)
      return
    }
    dispatch(userActions.userConsent(token))
      .then(() => setStep(2))
      .catch(error => console.log(error))
  }

  const handleDeny = () => {
    dispatch(userActions.denyConsent(token))
      .then(() => {
        setCancelled(true)
        setCancelModal(false)
      })
  }

  if (cancelled) {
    return (
      <div className="container text-center">
        <div className="success">
          <div className="icon icon-cell animate">done</div>
        </div>
        <h1>Electronic Delivery Denied</h1>
        <p className="info">
          Your lender will be notified.<br/>
          Your appraisal will be sent to you in the mail.
        </p>
        <p className="info">To restart the process click the button below.</p>
        <button
          className="button"
          data-testid="consent-restart-button"
          onClick={() => {
            setStep(1)
            setCancelled(false)
          }}
        >
          Return to Electronic Consent Form
        </button>
      </div>
    )
  }

  return (
    <div className="container text-center">
      {cancelModal && (
        <AlertModal
          closeText="No"
          submitText="Yes"
          onClose={() => setCancelModal(false)}
          onSubmit={handleDeny}
        >
          <div>Are you sure you want to deny Electronic Delivery?</div>
        </AlertModal>
      )}
      {downloading && <Loading/>}

      {step === 0 && (
        <div className="box bg">
          <h1>This link was sent in error.</h1>
          <p>Please disregard the email.</p>
        </div>
      )}

      {step === 1 && (
        <div className="box bg">
          <h1>Electronic Delivery Consent</h1>
          <p className="info">
            Review the terms below before downloading your appraisal.
          </p>
          <ScrollBar className="scrollable-text">
            <ConsentLegalText/>
          </ScrollBar>
          <form className="radio-form">
            <div className="radio-line">
              <input
                className="radio"
                type="radio"
                name="consent"
                value={true}
                data-testid="consent-yes-radio"
                onChange={(e) => setConsent((/true/i).test(e.target.value))}
              />
              <span>I consent to receive the appraisal report in electronic form</span>
            </div>
            <div className="radio-line">
              <input
                className="radio"
                type="radio"
                name="consent"
                value={false}
                data-testid="consent-no-radio"
                onChange={(e) => setConsent((/true/i).test(e.target.value))}
              />
              <span>I decline to receive the appraisal report electronically</span>
            </div>
          </form>
          <div style={{ marginTop: '1.5em' }}>
            <button
              disabled={consent === null}
              className={'button' + (consent === null ? '-disabled' : '')}
              data-testid="consent-confirm-button"
              onClick={handleConsentSubmit}
            >
              Confirm
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="box bg">
          <h1>Download Your Appraisal</h1>
          <p className="info">
            Enter your authorization code to download your appraisal.
          </p>

          <button
            className="button no-transform"
            data-testid="consent-view-code-button"
            style={{ marginTop: '0.5em' }}
            onClick={handleDownloadTestPDF}
          >
            View Authorization Code
          </button>
          <p className="caption" style={{ marginTop: '0.75em' }}>
            This will open a PDF with your code in a new tab.
          </p>

          <div style={{ marginTop: '2em' }}>
            <Input
              input={{
                type: 'text',
                placeholder: 'Enter Authorization Code',
                'data-testid': 'consent-auth-code-input',
                maxLength: 6,
                autoComplete: 'off',
                spellCheck: false,
                inputMode: 'text',
              }}
              style={{
                maxWidth: '14em',
                fontSize: '1.4em',
                textAlign: 'center',
              }}
              onChange={handleAuthCodeChange}
              field="authCode"
              value={authCode}
            />
          </div>

          {codeError && (
            <p className="error" role="alert" style={{ marginTop: '0.75em' }}>{codeError}</p>
          )}

          <button
            disabled={!authCode || downloading}
            className={classnames('no-transform', {
              button: authCode && !downloading,
              'button-disabled': !authCode || downloading,
            })}
            data-testid="consent-download-appraisal-button"
            style={{ marginTop: '1.5em' }}
            onClick={handleSubmit}
          >
            {downloading ? 'Downloading…' : 'Download Appraisal'}
          </button>

          {eConsentSettings && (
            <div style={{ marginTop: '1em' }}>
              <button
                data-testid="consent-change-button"
                onClick={() => {
                  setStep(1)
                  setConsent(null)
                }}
                style={{
                  padding: '0.5em 2em',
                  background: '#fff',
                  border: '2px solid #ccc',
                  borderRadius: '0.25em',
                  color: '#333',
                  fontSize: '0.9em',
                  cursor: 'pointer',
                }}
              >
                Change electronic delivery consent
              </button>
            </div>
          )}
        </div>
      )}

      {step === 3 && (
        <div className="container">
          <div className="success">
            <div className="icon icon-cell animate">done</div>
          </div>
          <h1>Appraisal Downloaded</h1>
          <p className="info">
            Your appraisal has been successfully downloaded.<br/>
            Thank you for using Reggora.
          </p>
        </div>
      )}

      {step === 4 && (
        <div className="box bg">
          <h1>Welcome Back!</h1>
          <p className="info">
            You have already downloaded your appraisal.<br/>
            You can download it again below.
          </p>
          <button
            className="button"
            data-testid="consent-redownload-appraisal-button"
            style={{ marginTop: '1em' }}
            onClick={triggerAppraisalDownload}
          >
            Download Appraisal
          </button>
        </div>
      )}

      {step === 5 && (
        <div className="box bg">
          <h1>Welcome Back!</h1>
          <p className="info">
            You denied electronic delivery of your appraisal.<br/>
            You can change your mind at any time.
          </p>
          <p className="info">To restart the process click the button below.</p>
          <button
            className="button"
            data-testid="consent-restart-button"
            style={{ marginTop: '1em' }}
            onClick={() => setStep(1)}
          >
            Return to Electronic Consent Form
          </button>
        </div>
      )}
    </div>
  )
}

export default StreamlinedConsent
